import { fetchAPI } from "./utils";

// ============= CompanyFileTag =============

export interface CompanyFileTag {
    id: string;
    name: string;
    fileCount: number;
}

export const getCompanyFileTags = async ({ token }: { token: string }): Promise<CompanyFileTag[]> => {
    return fetchAPI<CompanyFileTag[]>("/get-company-file-tags", "GET", undefined, token);
};

export const createCompanyFileTag = async ({
    token,
    name,
}: {
    token: string;
    name: string;
}): Promise<{ id: string }> => {
    return fetchAPI<{ id: string }>("/create-company-file-tag", "POST", { name }, token);
};

export const updateCompanyFileTag = async ({
    token,
    tagId,
    newName,
}: {
    token: string;
    tagId: string;
    newName: string;
}): Promise<void> => {
    return fetchAPI<void>("/update-company-file-tag", "POST", { tagId, newName }, token);
};

export const deleteCompanyFileTag = async ({
    token,
    tagId,
}: {
    token: string;
    tagId: string;
}): Promise<{ fileCount?: number }> => {
    return fetchAPI<{ fileCount?: number }>("/delete-company-file-tag", "POST", { tagId }, token);
};

// ============= CompanyUsageTypeMetadata =============

export interface CompanyUsageTypeMetadata {
    id: string;
    name: string;
    fileCount: number;
}

export const getCompanyUsageTypeMetadatas = async ({
    token,
}: {
    token: string;
}): Promise<CompanyUsageTypeMetadata[]> => {
    return fetchAPI<CompanyUsageTypeMetadata[]>("/get-company-usage-type-metadatas", "GET", undefined, token);
};

export const createCompanyUsageTypeMetadata = async ({
    token,
    name,
}: {
    token: string;
    name: string;
}): Promise<{ id: string }> => {
    return fetchAPI<{ id: string }>("/create-company-usage-type-metadata", "POST", { name }, token);
};

export const updateCompanyUsageTypeMetadata = async ({
    token,
    usageTypeMetadataId,
    newName,
}: {
    token: string;
    usageTypeMetadataId: string;
    newName: string;
}): Promise<void> => {
    return fetchAPI<void>("/update-company-usage-type-metadata", "POST", { usageTypeMetadataId, newName }, token);
};

export const deleteCompanyUsageTypeMetadata = async ({
    token,
    usageTypeMetadataId,
}: {
    token: string;
    usageTypeMetadataId: string;
}): Promise<{ fileCount?: number }> => {
    return fetchAPI<{ fileCount?: number }>(
        "/delete-company-usage-type-metadata",
        "POST",
        { usageTypeMetadataId },
        token,
    );
};

// ============= CompanyRightsTermMetadata =============

export interface CompanyRightsTermMetadata {
    id: string;
    name: string;
    fileCount: number;
}

export const getCompanyRightsTermMetadatas = async ({
    token,
}: {
    token: string;
}): Promise<CompanyRightsTermMetadata[]> => {
    return fetchAPI<CompanyRightsTermMetadata[]>("/get-company-rights-term-metadatas", "GET", undefined, token);
};

export const createCompanyRightsTermMetadata = async ({
    token,
    name,
}: {
    token: string;
    name: string;
}): Promise<{ id: string }> => {
    return fetchAPI<{ id: string }>("/create-company-rights-term-metadata", "POST", { name }, token);
};

export const updateCompanyRightsTermMetadata = async ({
    token,
    rightsTermMetadataId,
    newName,
}: {
    token: string;
    rightsTermMetadataId: string;
    newName: string;
}): Promise<void> => {
    return fetchAPI<void>("/update-company-rights-term-metadata", "POST", { rightsTermMetadataId, newName }, token);
};

export const deleteCompanyRightsTermMetadata = async ({
    token,
    rightsTermMetadataId,
}: {
    token: string;
    rightsTermMetadataId: string;
}): Promise<{ fileCount?: number }> => {
    return fetchAPI<{ fileCount?: number }>(
        "/delete-company-rights-term-metadata",
        "POST",
        { rightsTermMetadataId },
        token,
    );
};

// ============= CompanyTerritoryMetadata =============

export interface CompanyTerritoryMetadata {
    id: string;
    name: string;
    fileCount: number;
}

export const getCompanyTerritoryMetadatas = async ({
    token,
}: {
    token: string;
}): Promise<CompanyTerritoryMetadata[]> => {
    return fetchAPI<CompanyTerritoryMetadata[]>("/get-company-territory-metadatas", "GET", undefined, token);
};

export const createCompanyTerritoryMetadata = async ({
    token,
    name,
}: {
    token: string;
    name: string;
}): Promise<{ id: string }> => {
    return fetchAPI<{ id: string }>("/create-company-territory-metadata", "POST", { name }, token);
};

export const updateCompanyTerritoryMetadata = async ({
    token,
    territoryMetadataId,
    newName,
}: {
    token: string;
    territoryMetadataId: string;
    newName: string;
}): Promise<void> => {
    return fetchAPI<void>("/update-company-territory-metadata", "POST", { territoryMetadataId, newName }, token);
};

export const deleteCompanyTerritoryMetadata = async ({
    token,
    territoryMetadataId,
}: {
    token: string;
    territoryMetadataId: string;
}): Promise<{ fileCount?: number }> => {
    return fetchAPI<{ fileCount?: number }>(
        "/delete-company-territory-metadata",
        "POST",
        { territoryMetadataId },
        token,
    );
};

// ============= CompanyAllowedMediaMetadata =============

export interface CompanyAllowedMediaMetadata {
    id: string;
    name: string;
    fileCount: number;
}

export const getCompanyAllowedMediaMetadatas = async ({
    token,
}: {
    token: string;
}): Promise<CompanyAllowedMediaMetadata[]> => {
    return fetchAPI<CompanyAllowedMediaMetadata[]>("/get-company-allowed-media-metadatas", "GET", undefined, token);
};

export const createCompanyAllowedMediaMetadata = async ({
    token,
    name,
}: {
    token: string;
    name: string;
}): Promise<{ id: string }> => {
    return fetchAPI<{ id: string }>("/create-company-allowed-media-metadata", "POST", { name }, token);
};

export const updateCompanyAllowedMediaMetadata = async ({
    token,
    allowedMediaMetadataId,
    newName,
}: {
    token: string;
    allowedMediaMetadataId: string;
    newName: string;
}): Promise<void> => {
    return fetchAPI<void>("/update-company-allowed-media-metadata", "POST", { allowedMediaMetadataId, newName }, token);
};

export const deleteCompanyAllowedMediaMetadata = async ({
    token,
    allowedMediaMetadataId,
}: {
    token: string;
    allowedMediaMetadataId: string;
}): Promise<{ fileCount?: number }> => {
    return fetchAPI<{ fileCount?: number }>(
        "/delete-company-allowed-media-metadata",
        "POST",
        { allowedMediaMetadataId },
        token,
    );
};
